<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { ArrowRight, Play } from 'lucide-vue-next'
import { appLoginUrl } from '@/config'
import PageHero from '@/components/marketing/PageHero.vue'
import { useSeo } from '@/composables/useSeo'
import { useJsonLd } from '@/composables/useJsonLd'
import {
  SOUND_TEXT, jobsText, lengthsText, otherVideoModelsByTier, speedText, videoModelBySlug, type VideoModelQuality,
} from '@/data/videoModels'
import { usePricingStore } from '@/stores/pricing'

// Vite-ssg only enumerates real slugs, so during prerender the model is always
// defined; on the client an unknown slug falls through to NotFound (as UseCaseView).
const route = useRoute()
const router = useRouter()
const SITE_URL = 'https://open-muse.com'
const pricingStore = usePricingStore()

const model = computed(() => videoModelBySlug(String(route.params.slug)))

onMounted(() => {
  if (!model.value) router.replace({ name: 'NotFound' })
  // Prices arrive at runtime only, so a prerendered page never carries a stale number.
  void pricingStore.ensureLoaded()
})

const m = model.value
if (m) {
  useSeo({ title: `${m.name} video model`, description: m.summary })
  const pageUrl = `${SITE_URL}/models/${m.slug}`
  useJsonLd([
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `${m.name} video model`,
      description: m.summary,
      url: pageUrl,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: m.name, item: pageUrl },
      ],
    },
  ])
}

const chipLabel = (q: VideoModelQuality) => (q.label ? `${q.label}: ${q.tier}` : q.tier)

// Live per-second token prices keyed `${modelId}|${quality}` lowercased; empty while loading or failed.
const rateByKey = computed(() => new Map(
  (pricingStore.pricing?.videoQualityCosts ?? []).map((r) => [`${r.modelId}|${r.quality}`.toLowerCase(), r.tokensPerSecond]),
))

// One row per quality with its live rate (null until the feed has it). A page can cover several
// catalog ids (Wan 2.2 Fast); the first id with a price for the quality wins.
const qualityRows = computed(() => {
  const mod = model.value
  if (!mod) return []
  return mod.qualities.map((q) => ({
    q,
    rate: mod.modelIds.map((id) => rateByKey.value.get(`${id}|${q.quality}`.toLowerCase())).find((r) => r != null) ?? null,
  }))
})

// One clip plays at a time: pressing a poster plays a muted looping video under the same button,
// pressing it again stops it, and pressing another sample puts the first back to its poster.
const playing = ref<string | null>(null)
function onClipMounted(el: unknown) {
  void (el as HTMLVideoElement | null)?.play?.()?.catch(() => {})
}

const related = computed(() => (model.value ? otherVideoModelsByTier(model.value) : []))
const ctaHref = appLoginUrl({ mode: 'signup' })

const rowClass = 'grid gap-1 border-t border-surface-800 py-4 sm:grid-cols-[10rem_1fr] sm:gap-6'
</script>

<template>
  <div v-if="model" class="min-h-screen bg-surface-950">
    <PageHero>
      <template #eyebrow>Video model</template>
      <template #title>{{ model.name }}</template>
      <template #description>{{ model.goodFor }}</template>
      <template #meta>
        <span
          v-for="q in model.qualities"
          :key="q.quality"
          class="text-xs font-medium text-surface-300 [&:not(:last-child)]:after:content-['·'] [&:not(:last-child)]:after:ml-2"
          data-model-tier
        >{{ chipLabel(q) }}</span>
      </template>
    </PageHero>

    <section class="px-6 pb-14 sm:px-8 lg:px-10">
      <div class="mx-auto max-w-3xl">
        <dl class="border-b border-surface-800" data-model-facts>
          <div :class="rowClass">
            <dt class="text-sm font-semibold text-surface-100">Speed</dt>
            <dd class="space-y-1 text-sm text-surface-300">
              <p v-for="q in model.qualities" :key="q.quality">
                <span class="text-surface-400">{{ q.label ?? q.quality }}: </span>{{ speedText(q) }}
              </p>
            </dd>
          </div>
          <div :class="rowClass">
            <dt class="text-sm font-semibold text-surface-100">What it takes</dt>
            <dd class="text-sm text-surface-300">{{ jobsText(model.jobs) }}</dd>
          </div>
          <div :class="rowClass">
            <dt class="text-sm font-semibold text-surface-100">Length</dt>
            <dd class="text-sm text-surface-300">{{ lengthsText(model.lengths) }}</dd>
          </div>
          <div v-if="model.sound" :class="rowClass">
            <dt class="text-sm font-semibold text-surface-100">Sound</dt>
            <dd class="text-sm text-surface-300">{{ SOUND_TEXT }}</dd>
          </div>
          <div :class="rowClass">
            <dt class="text-sm font-semibold text-surface-100">Cost</dt>
            <dd class="space-y-1 text-sm text-surface-300">
              <p v-for="{ q, rate } in qualityRows" :key="q.quality">
                <span class="text-surface-400">{{ q.label ?? q.quality }}: </span>
                <span v-if="rate !== null" class="font-semibold text-brass-400">{{ rate }} {{ rate === 1 ? 'token' : 'tokens' }} a second</span>
                <RouterLink v-else to="/pricing" class="text-primary-300 underline-offset-2 hover:underline">See current prices</RouterLink>
              </p>
            </dd>
          </div>
        </dl>
      </div>
    </section>

    <section class="px-6 pb-14 sm:px-8 lg:px-10">
      <div class="mx-auto grid max-w-3xl gap-8 sm:grid-cols-2">
        <div>
          <h2 class="mb-3 text-xl font-bold text-surface-50">Good at</h2>
          <ul class="list-disc space-y-2 pl-5 text-sm leading-relaxed text-surface-300 marker:text-surface-500">
            <li v-for="s in model.strengths" :key="s">{{ s }}</li>
          </ul>
        </div>
        <div>
          <h2 class="mb-3 text-xl font-bold text-surface-50">Watch for</h2>
          <ul class="list-disc space-y-2 pl-5 text-sm leading-relaxed text-surface-300 marker:text-surface-500">
            <li v-for="l in model.limits" :key="l">{{ l }}</li>
          </ul>
        </div>
      </div>
    </section>

    <section v-if="model.samples.length > 0" class="px-6 pb-14 sm:px-8 lg:px-10">
      <div class="mx-auto max-w-3xl">
        <h2 class="mb-4 text-xl font-bold text-surface-50">Samples</h2>
        <div :class="['grid grid-cols-2 gap-3', model.samples.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2']">
          <figure v-for="s in model.samples" :key="s.src" class="overflow-hidden rounded-card border border-surface-800 bg-surface-900" :class="s.shape === 'wide' ? 'col-span-full' : ''">
            <div :class="['relative', s.shape === 'wide' ? 'aspect-video' : 'aspect-[3/4]']">
              <video
                v-if="playing === s.src"
                :ref="onClipMounted"
                :src="s.src"
                :poster="s.poster"
                muted
                playsinline
                autoplay
                loop
                aria-hidden="true"
                class="absolute inset-0 h-full w-full object-cover"
              />
              <!-- The button stays mounted while the clip plays, so focus never leaves it. -->
              <button
                type="button"
                class="group relative block h-full w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-400"
                :aria-label="playing === s.src ? `Stop sample: ${s.alt}` : `Play sample: ${s.alt}`"
                :aria-pressed="playing === s.src"
                data-sample-play
                @click="playing = playing === s.src ? null : s.src"
              >
                <template v-if="playing !== s.src">
                  <img :src="s.poster" :alt="s.alt" loading="lazy" decoding="async" class="h-full w-full object-cover" />
                  <span class="absolute inset-0 flex items-center justify-center">
                    <span class="flex h-12 w-12 items-center justify-center rounded-full bg-surface-950/70 text-white transition-colors group-hover:bg-surface-950/90">
                      <Play class="h-5 w-5" />
                    </span>
                  </span>
                </template>
              </button>
            </div>
            <figcaption v-if="s.prompt" class="p-2 text-xs text-surface-400">{{ s.prompt }}</figcaption>
          </figure>
        </div>
      </div>
    </section>

    <section v-if="related.length > 0" class="border-t border-surface-800 bg-surface-900 px-6 py-12 sm:px-8 lg:px-10">
      <div class="mx-auto max-w-3xl space-y-6">
        <div v-for="g in related" :key="g.tier">
          <h2 class="mb-3 text-lg font-bold text-surface-50">Other models in {{ g.tier }}</h2>
          <ul class="flex flex-wrap gap-2">
            <li v-for="o in g.models" :key="o.slug">
              <RouterLink
                :to="`/models/${o.slug}`"
                :data-related-model="o.slug"
                class="inline-flex min-h-11 items-center rounded-full border border-surface-700 bg-surface-800 px-4 text-sm text-surface-200 transition-colors hover:text-primary-300"
              >{{ o.name }}</RouterLink>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section class="px-6 py-14 text-center sm:px-8 lg:px-10">
      <a
        :href="ctaHref"
        class="inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-primary-600 px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-primary-700 sm:w-auto"
      >
        Try {{ model.name }}
        <ArrowRight class="h-4 w-4" />
      </a>
    </section>
  </div>
</template>
